import React, { useMemo } from 'react';

import { ChromelessEditor as PlatformChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import type { RenderFn } from '@atlassian/forge-ui-types';

import type { EditorProps } from '../../../types';
import { usePresetByFeatures } from '../utils/usePresetByFeatures';

export type ChromelessEditorProps = EditorProps;

type WrapperProps = { children: React.ReactNode };

const NoWrapper = ({ children }: WrapperProps) => <>{children}</>;

const FlaggedContext = componentWithFG(
	'forge-ui-fix-editor-components-in-editor',
	EditorContext,
	NoWrapper,
);

/**
 * The chromeless editor provides the Editor without any of the standard UI features. It's ideal for cases where the integrator wants complete control and responsibility over the editor UI.
 */
export const ChromelessEditor = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { onChange, defaultValue, features, isDisabled } = props.forgeDoc
		.props as ChromelessEditorProps;

	const transformer = useMemo(() => new JSONTransformer(), []);

	const { preset } = usePresetByFeatures('chromeless', features);

	return (
		<FlaggedContext>
			<PlatformChromelessEditor
				disabled={isDisabled}
				defaultValue={defaultValue}
				preset={preset}
				onChange={(editorView) => {
					const jsonDocNode = transformer.encode(editorView.state.doc);
					onChange?.(jsonDocNode);
				}}
			/>
		</FlaggedContext>
	);
};
